/**
 * GraphQueryBuilder - Fluent API for building GraphQL queries
 *
 * DESIGN PRINCIPLES:
 * - Fluent interface for composability
 * - Immutable operations (returns new instance)
 * - Type-safe filter building
 * - Field validation against allowedColumns
 */

import type {
  Filter,
  GraphQuery,
  NodeLabel,
  Operator,
  Relationship,
} from '@/types';

/**
 * Allowed columns for graph queries
 * Source: ./modules/chariot/backend/build/pkg/query/read.go:41-85
 */
export const ALLOWED_COLUMNS = new Set([
  'key',
  'identifier',
  'group',
  'dns',
  'name',
  'value',
  'status',
  'source',
  'origin',
  'created',
  'registrar',
  'registrant',
  'email',
  'country',
  'priority',
  'class',
  'type',
  'title',
  'visited',
  'updated',
  'vendor',
  'product',
  'version',
  'cpe',
  'asname',
  'asnumber',
  'cvss',
  'epss',
  'kev',
  'exploit',
  'private',
  'id',
  'writeupId',
  'category',
  'attackSurface',
  'capability',
  'cloudService',
  'cloudId',
  'cloudRoot',
  'cloudAccount',
  'plextracid',
  'beta',
  'tags',
  'origins',
]);

/**
 * Validate field name against allowed columns
 */
function validateField(field: string): void {
  if (field && !ALLOWED_COLUMNS.has(field)) {
    console.warn(
      `Field "${field}" is not in allowedColumns. Query may fail validation.`
    );
  }
}

/**
 * GraphQueryBuilder class - Fluent API for building graph queries
 */
export class GraphQueryBuilder {
  private _labels: readonly NodeLabel[];
  private _filters: readonly Filter[];
  private _relationships: readonly Relationship[];
  private _orderBy?: string;
  private _descending?: boolean;

  constructor(
    labels: readonly NodeLabel[] = [],
    filters: readonly Filter[] = [],
    relationships: readonly Relationship[] = [],
    orderBy?: string,
    descending?: boolean
  ) {
    this._labels = labels;
    this._filters = filters;
    this._relationships = relationships;
    this._orderBy = orderBy;
    this._descending = descending;
  }

  /**
   * Set node labels
   */
  labels(...labels: NodeLabel[]): GraphQueryBuilder {
    return new GraphQueryBuilder(
      labels,
      this._filters,
      this._relationships,
      this._orderBy,
      this._descending
    );
  }

  /**
   * Add a filter
   */
  filter(
    field: string,
    operator: Operator,
    value: Filter['value'],
    not = false
  ): GraphQueryBuilder {
    validateField(field);

    const newFilter: Filter = {
      field,
      operator,
      value,
      ...(not && { not: true }),
    };

    return new GraphQueryBuilder(
      this._labels,
      [...this._filters, newFilter],
      this._relationships,
      this._orderBy,
      this._descending
    );
  }

  /**
   * Add multiple filters
   */
  filters(filters: readonly Filter[]): GraphQueryBuilder {
    // Validate all fields
    filters.forEach(f => validateField(f.field));

    return new GraphQueryBuilder(
      this._labels,
      [...this._filters, ...filters],
      this._relationships,
      this._orderBy,
      this._descending
    );
  }

  /**
   * Add an OR filter group
   */
  orFilters(filters: readonly Filter[]): GraphQueryBuilder {
    // Validate all fields
    filters.forEach(f => validateField(f.field));

    const orFilter: Filter = {
      field: '',
      operator: 'OR',
      value: [...filters],
    };

    return new GraphQueryBuilder(
      this._labels,
      [...this._filters, orFilter],
      this._relationships,
      this._orderBy,
      this._descending
    );
  }

  /**
   * Add an AND filter group
   */
  andFilters(filters: readonly Filter[]): GraphQueryBuilder {
    // Validate all fields
    filters.forEach(f => validateField(f.field));

    const andFilter: Filter = {
      field: '',
      operator: 'AND',
      value: [...filters],
    };

    return new GraphQueryBuilder(
      this._labels,
      [...this._filters, andFilter],
      this._relationships,
      this._orderBy,
      this._descending
    );
  }

  /**
   * Add a relationship
   */
  relationship(relationship: Relationship): GraphQueryBuilder {
    return new GraphQueryBuilder(
      this._labels,
      this._filters,
      [...this._relationships, relationship],
      this._orderBy,
      this._descending
    );
  }

  /**
   * Add multiple relationships
   */
  relationships(relationships: readonly Relationship[]): GraphQueryBuilder {
    return new GraphQueryBuilder(
      this._labels,
      this._filters,
      [...this._relationships, ...relationships],
      this._orderBy,
      this._descending
    );
  }

  /**
   * Set ordering
   */
  orderBy(field: string, descending = false): GraphQueryBuilder {
    validateField(field);

    return new GraphQueryBuilder(
      this._labels,
      this._filters,
      this._relationships,
      field,
      descending
    );
  }

  /**
   * Build the final query
   */
  build(): GraphQuery {
    return {
      node: {
        labels: [...this._labels],
        filters: [...this._filters],
        ...(this._relationships.length > 0 && {
          relationships: this._relationships as any,
        }),
      },
      ...(this._orderBy && { orderBy: this._orderBy }),
      ...(this._descending !== undefined && { descending: this._descending }),
    };
  }

  /**
   * Create a new builder from scratch
   */
  static create(labels?: NodeLabel[]): GraphQueryBuilder {
    return new GraphQueryBuilder(labels || []);
  }

  /**
   * Clone an existing query
   */
  static fromQuery(query: GraphQuery): GraphQueryBuilder {
    // Note: relationships in GraphQuery are objects, but builder uses string labels
    // This is a known limitation of the current API
    return new GraphQueryBuilder(
      query.node.labels || [],
      query.node.filters || [],
      [],
      query.orderBy,
      query.descending
    );
  }
}
